import graphql from 'babel-plugin-relay/macro';
import React from 'react';
import {usePaginationFragment, useMutation} from 'react-relay/hooks';
import ConnectionHandler from 'relay-connection-handler-plus';
import ImportantTextCell from 'src/components/table/cell-types/ImportantTextCell';
import useFilters from 'src/hooks/useFilters';
import {showEventNotification} from 'src/utils/notifications';

import Table from '../../../components/table';
import SecondaryTextCell from '../../../components/table/cell-types/SecondaryTextCell';
import {AdminUsersOrder} from './__generated__/AdminUsersPaginationQuery.graphql';
import {listPageAdminUsersQuery} from './__generated__/listPageAdminUsersQuery.graphql';
import {UsersTable_adminUsers$key} from './__generated__/UsersTable_adminUsers.graphql';
import {UsersTableDeleteAdminUserMutation} from './__generated__/UsersTableDeleteAdminUserMutation.graphql';
import TableFilters from './TableFilters';

type Props = {
  users: UsersTable_adminUsers$key;
};

type Filters = {
  text?: string;
  orderBy?: AdminUsersOrder;
};

function UsersTable(props: Props) {
  const {data, loadNext, hasNext, refetch} = usePaginationFragment<
    listPageAdminUsersQuery,
    UsersTable_adminUsers$key
  >(
    graphql`
      fragment UsersTable_adminUsers on Query
        @argumentDefinitions(
          first: {type: "Int!"}
          after: {type: "String"}
          text: {type: "String"}
          orderBy: {type: "AdminUsersOrder"}
        )
        @refetchable(queryName: "AdminUsersPaginationQuery") {
        adminUsers(first: $first, after: $after, orderBy: $orderBy, text: $text)
          @connection(key: "UsersList_adminUsers") {
          totalCount
          pageInfo {
            hasPreviousPage
            hasNextPage
            startCursor
            endCursor
          }
          edges {
            node {
              id
              createdAt
              firstName
              lastName
              phone
              email
            }
          }
        }
      }
    `,
    props.users,
  );

  const [commit] = useMutation<UsersTableDeleteAdminUserMutation>(deleteUserMutation);
  const {addFilters, setFilters, isTransitioning} = useFilters<Filters>({}, refetch);

  const adminUsers = data.adminUsers || {totalCount: 0, edges: []};

  return (
    <Table
      filters={() => (
        <TableFilters onApplyFilters={handleOnApplyFilter} onResetFilters={handleOnResetFilter} />
      )}
      actions={{show: false, delete: true, edit: true, create: true}}
      totalDataLength={adminUsers.totalCount}
      columns={columns}
      isTransitioning={isTransitioning}
      onPaginate={() => hasNext && loadNext(50)}
      onSort={handleOnSort}
      onDelete={handleDelete}
      data={adminUsers.edges}
    />
  );

  function handleOnApplyFilter(filters: {text: string}) {
    addFilters({text: filters.text});
  }

  function handleOnResetFilter() {
    setFilters({});
  }

  async function handleOnSort(sortByConfig: any[]) {
    if (!sortByConfig.length) {
      addFilters({orderBy: undefined});

      return;
    }

    const orderByDirection = sortByConfig[0].desc ? 'DESC' : 'ASC';
    const orderByField = sortByConfig[0].id.toUpperCase();

    addFilters({orderBy: {direction: orderByDirection, field: orderByField}});
  }

  function handleDelete(id: string) {
    commit({
      variables: {input: {adminUserId: id}},
      onCompleted: payload => {
        const entry = payload.deleteAdminUser?.adminUser;

        if (entry) {
          showEventNotification('removed', 'User', entry.firstName);
        }
      },
      updater: store => {
        const root = store.getRoot();

        const connections = ConnectionHandler.getConnections(root, 'UsersList_adminUsers');

        if (!connections) {
          return;
        }

        connections.forEach(connection => {
          ConnectionHandler.deleteNode(connection, id);
        });
      },
    });
  }
}

export default UsersTable;

const deleteUserMutation = graphql`
  mutation UsersTableDeleteAdminUserMutation($input: DeleteAdminUserInput!) {
    deleteAdminUser(input: $input) {
      adminUser {
        id
        firstName
        createdAt
      }
      errors {
        code
        message
      }
    }
  }
`;

const columns = [
  {
    Header: 'First Name',
    accessor: 'node.firstName',
    id: 'first_name',
    Cell({cell: {value}}: any) {
      return <SecondaryTextCell value={value} />;
    },
  },
  {
    Header: 'Last Name',
    accessor: 'node.lastName',
    id: 'last_name',
    Cell({cell: {value}}: any) {
      return <ImportantTextCell value={value} />;
    },
  },
  {
    Header: 'Email',
    accessor: 'node.email',
    id: 'email',
    Cell({cell: {value}}: any) {
      return <SecondaryTextCell value={value} />;
    },
  },
  {
    Header: 'Phone',
    accessor: 'node.phone',
    id: 'phone',
    Cell({cell: {value}}: any) {
      return <ImportantTextCell value={value} />;
    },
  },
];
